import { pick } from 'underscore';
import { IntegrationPostProcessor } from 'components/VideoTemplateEditor/types/integrations';
import { getVideoConfigFromUCS } from 'utils/embed/video';
import { measurementToString } from 'utils/placement';
import { getStateContent } from '../core/selectors';
import { getCaptionsExportConfig } from '../modules/captions/selectors';
import { getProgressAnimationOptions } from '../modules/progress';
import { exportTextOverlays } from '../modules/text';
import { getTimerOptions } from '../modules/timer';
import { getSoundwave } from '../modules/waveform';
import { VideoTemplateState } from '../types';
import { getEdgeVideosOptions } from './edge';
import { getImageTypeForSlideExport, getSourceImageOrigin } from './slideshow';
import { TemplateEditorStateExport } from './types';
import { getPlacementFromVideoClip } from './video';

export function exportState(
  state: VideoTemplateState,
  postProcessors: IntegrationPostProcessor[] = [],
): TemplateEditorStateExport {
  const content = getStateContent(state);
  const {
    audioInfo,
    canvas,
    introOutro,
    layers,
    mainImage,
    slideEffectText,
    slideshow,
    watermark,
    videoClips,
  } = content;

  const soundwave = getSoundwave(content);
  const trackTypes = layers.order.map(id => layers.data[id].type);

  const exportedState: TemplateEditorStateExport = {
    audioInfo,
    captions: getCaptionsExportConfig(content),
    soundwave,
    backgroundColor: content.backgroundColor,
    edgeVideos: getEdgeVideosOptions(introOutro),
    layers: trackTypes,
    mainImage: !mainImage?.ids.length
      ? undefined
      : {
          indexes: mainImage?.ids
            .map(id => slideshow.order.indexOf(id))
            .filter(id => id !== -1),
        },
    lockSpecUuid: content.template?.previewConfiguration?.lockSpec?.uuid,
    progress: getProgressAnimationOptions(content),
    slideshow: !slideshow
      ? undefined
      : slideshow.order.map(slideId => {
          const stateSlide = slideshow.data[slideId];
          const embeddedTexts = stateSlide.imageEffect.options?.embeddedTexts;

          return {
            ...stateSlide,
            layerId: layers.order.indexOf(slideshow.data[slideId].layerId),
            placement: measurementToString(slideshow.data[slideId].placement),
            sourceImageOrigin: getSourceImageOrigin(slideshow.data[slideId]),
            blurRadius: slideshow.data[slideId].blurRadius?.toString(),
            borderRadius: slideshow.data[slideId].borderRadius?.toString(),
            imageType: getImageTypeForSlideExport(slideshow.data[slideId]),
            ...(!embeddedTexts
              ? undefined
              : {
                  imageEffect: {
                    ...stateSlide.imageEffect,
                    options: {
                      ...stateSlide.imageEffect.options,
                      embeddedTexts: embeddedTexts.map(textId => {
                        const effectText = slideEffectText[textId];
                        return pick(effectText, 'text', 'textType');
                      }),
                    },
                  },
                }),
          };
        }),
    templateId: content.template?.templateId,
    textOverlays: exportTextOverlays(content),
    timer: getTimerOptions(content),
    watermark: !watermark
      ? undefined
      : {
          ...watermark,
          position: {
            left: watermark.position.left.toUnit('vw', canvas).toString(),
            top: watermark.position.top.toUnit('vh', canvas).toString(),
          },
          size: {
            height: watermark.size.height.toUnit('vh', canvas).toString(),
            width: watermark.size.width.toUnit('vw', canvas).toString(),
          },
        },

    videoClips: !videoClips
      ? undefined
      : videoClips.order.map(videoClipId => {
          const videoClip = videoClips.data[videoClipId];
          const { layerId, original, placement, enableBlur, metadata } =
            videoClip ?? {};
          const layerIndex = layers.order.indexOf(layerId);

          return getVideoConfigFromUCS(
            original,
            canvas,
            layerIndex,
            placement || getPlacementFromVideoClip(original, canvas),
            enableBlur,
            metadata,
          );
        }),
  };

  return postProcessors.reduce((current, processor) => {
    return processor(current, content);
  }, exportedState);
}
